import { getTestimonials } from 'api/user/profile-api'
import Image from 'next/image'
import React, { useState, useCallback } from 'react'
import { FaStar } from 'react-icons/fa'
import { FaAngleDown } from 'react-icons/fa6'
import { s3Static } from 'utils/constants'
import { CustomerReviews } from '../../commons/CustomerReviews'

// UAE / Malaysia pass `title` and `showRating` (features.googleRating);
// Singapore passes none.
export const TrainerCustomerReviews = (props) => {
    const { title, showRating = true } = props
    const [testimonials, setTestimonials] = useState(props.testimonials)
    const [pageNumber, setPageNumber] = useState(9)
    const pageSize = 6
    const [isShowLoadMore, setIsShowLoadMore] = useState(true)
    const [isLoading, setIsLoading] = useState(false)

    const loadMoreReviews = useCallback(async () => {
        if (isLoading || !isShowLoadMore) return
        setIsLoading(true)
        try {
            const response = await getTestimonials("Fitness Trainer", pageNumber, pageSize)
            if (response && response.length > 0) {
                setPageNumber(prev => prev + pageSize)
                setTestimonials(prev => [...prev, ...response])
            }
            if (!response || response.length < pageSize) {
                setIsShowLoadMore(false)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }, [isLoading, isShowLoadMore, pageNumber])

    // Wrapper for CustomerReviews scroll-based load more (returns Promise)
    const handleScrollLoadMore = async () => {
        return loadMoreReviews()
    }

    return (
        <section className="relative py-6 md:py-16 overflow-hidden bg-slate-900">
            {/* Mesh gradient blobs */}
            <div className="absolute top-0 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl" />

            <div className="relative max-w-6xl mx-auto px-4">
                {/* Section Header */}
                <div className="text-center mb-8 md:mb-10">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                        <span className="text-sm">💬</span>
                        <span className="text-xs font-medium text-white/70">Client Reviews</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4 tracking-tight">
                        {title ?? 'Personal Trainer Reviews from Singapore Clients'}
                    </h2>

                    {/* Google Rating Badge */}
                    {showRating &&
                    <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/20">
                        <Image
                            src={s3Static + "google-icon.png"}
                            height={50}
                            width={60}
                            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                            alt="Google Reviews"
                        />
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-white">4.9</span>
                            <div className="flex text-amber-400">
                                {[...Array(5)].map((_, i) => (
                                    <FaStar key={i} className="text-lg" />
                                ))}
                            </div>
                        </div>
                    </div>
                    }
                </div>

                {/* Reusable CustomerReviews — Dark Theme */}
                <CustomerReviews
                    testimonials={testimonials}
                    onLoadMore={handleScrollLoadMore}
                    isLoadingExternal={isLoading}
                    hasMoreExternal={isShowLoadMore}
                    theme="dark"
                    providerLabel="Trainer"
                />

                {/* Show More Reviews - Desktop only */}
                {isShowLoadMore && (
                    <div className="hidden md:block text-center mt-8">
                        <button
                            type='button'
                            onClick={loadMoreReviews}
                            disabled={isLoading}
                            aria-label='Load more reviews'
                            className='inline-flex items-center gap-2 px-6 py-3 text-orange-400 hover:text-orange-300 font-semibold rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-300 disabled:opacity-50'
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    Show More Reviews
                                    <FaAngleDown />
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    )
}
